import React from "react";
import AdminNavbar from "./AdminNavbar";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grow,
    IconButton,
    Paper,
    Snackbar,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import AdminData from "../../backend/dataservices/AdminData";
import {fetchIsTenantAdmin} from "../../utils/TenantAdminAccess";

function AdminTenantsPage() {

    const [tenants, setTenants] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [accessAllowed, setAccessAllowed] = React.useState(null);

    React.useEffect(() => {
        fetchIsTenantAdmin().then(setAccessAllowed);
    }, []);

    // Add dialog
    const [addOpen, setAddOpen] = React.useState(false);
    const [newCode, setNewCode] = React.useState('');
    const [newName, setNewName] = React.useState('');
    const [addSaving, setAddSaving] = React.useState(false);

    // Edit dialog
    const [editOpen, setEditOpen] = React.useState(false);
    const [editTenant, setEditTenant] = React.useState(null);
    const [editCode, setEditCode] = React.useState('');
    const [editName, setEditName] = React.useState('');
    const [orgCodes, setOrgCodes] = React.useState([]);
    const [orgCodesLoading, setOrgCodesLoading] = React.useState(false);
    const [newOrgCode, setNewOrgCode] = React.useState('');
    const [editSaving, setEditSaving] = React.useState(false);

    // Snackbar
    const [snackbarOpen, setSnackbarOpen] = React.useState(false);
    const [snackbarText, setSnackbarText] = React.useState('');
    const [snackbarSeverity, setSnackbarSeverity] = React.useState('success');

    function showSnackbar(text, severity = 'success') {
        setSnackbarText(text);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    }

    function loadTenants() {
        setLoading(true);
        AdminData.getTenants().then(data => {
            setTenants(Array.isArray(data) ? data : []);
            setLoading(false);
        }).catch(() => {
            setLoading(false);
            showSnackbar('Fehler beim Laden der Tenants.', 'error');
        });
    }

    React.useEffect(() => {
        loadTenants();
    }, []);

    // --- Add dialog ---

    function handleOpenAdd() {
        setNewCode('');
        setNewName('');
        setAddOpen(true);
    }

    function handleCloseAdd() {
        setAddOpen(false);
    }

    function handleAddTenant() {
        if (!newCode.trim() || !newName.trim()) return;
        setAddSaving(true);
        AdminData.createTenant({ T_Code: newCode.trim(), T_Name: newName.trim() }).then(() => {
            setAddSaving(false);
            setAddOpen(false);
            showSnackbar('Tenant erfolgreich angelegt.');
            loadTenants();
        }).catch(err => {
            setAddSaving(false);
            if (err?.response?.status === 409) {
                showSnackbar('T_Code ist bereits vergeben.', 'error');
            } else {
                showSnackbar('Fehler beim Anlegen des Tenants.', 'error');
            }
        });
    }

    // --- Edit dialog ---

    function handleOpenEdit(tenant) {
        setEditTenant(tenant);
        setEditCode(tenant.T_Code);
        setEditName(tenant.T_Name);
        setNewOrgCode('');
        setOrgCodes([]);
        setEditOpen(true);
        setOrgCodesLoading(true);
        AdminData.getOrgCodes(tenant.T_ID).then(data => {
            setOrgCodes(Array.isArray(data) ? data : []);
            setOrgCodesLoading(false);
        }).catch(() => {
            setOrgCodesLoading(false);
            showSnackbar('Fehler beim Laden der OrgCodes.', 'error');
        });
    }

    function handleCloseEdit() {
        setEditOpen(false);
        setEditTenant(null);
    }

    function handleSaveTenant() {
        if (!editCode.trim() || !editName.trim()) return;
        setEditSaving(true);
        AdminData.updateTenant(editTenant.T_ID, { T_Code: editCode.trim(), T_Name: editName.trim() }).then(() => {
            setEditSaving(false);
            setEditOpen(false);
            showSnackbar('Tenant erfolgreich gespeichert.');
            loadTenants();
        }).catch(err => {
            setEditSaving(false);
            if (err?.response?.status === 409) {
                showSnackbar('T_Code ist bereits vergeben.', 'error');
            } else {
                showSnackbar('Fehler beim Speichern des Tenants.', 'error');
            }
        });
    }

    function handleAddOrgCode() {
        if (!newOrgCode.trim()) return;
        AdminData.addOrgCode(editTenant.T_ID, newOrgCode.trim()).then(result => {
            setOrgCodes(prev => [...prev, { TOC_ID: result.TOC_ID, TOC_OrgCode: newOrgCode.trim() }]);
            setNewOrgCode('');
        }).catch(err => {
            if (err?.response?.status === 409) {
                showSnackbar('Dieser OrgCode ist bereits einem Tenant zugewiesen.', 'error');
            } else {
                showSnackbar('Fehler beim Hinzufügen des OrgCodes.', 'error');
            }
        });
    }

    function handleToggleHidden(tenant) {
        const newHidden = !tenant.T_Hidden;
        AdminData.setTenantHidden(tenant.T_ID, newHidden).then(() => {
            setTenants(prev => prev.map(t =>
                t.T_ID === tenant.T_ID ? { ...t, T_Hidden: newHidden } : t
            ));
            showSnackbar(newHidden ? 'Tenant ausgeblendet.' : 'Tenant eingeblendet.');
        }).catch(() => {
            showSnackbar('Fehler beim Ändern des Status.', 'error');
        });
    }

    function handleToggleLocked(tenant) {
        const newLocked = !tenant.T_Locked;
        AdminData.setTenantLocked(tenant.T_ID, newLocked).then(() => {
            setTenants(prev => prev.map(t =>
                t.T_ID === tenant.T_ID ? { ...t, T_Locked: newLocked } : t
            ));
            showSnackbar(newLocked ? 'Tenant gesperrt.' : 'Tenant entsperrt.');
        }).catch(() => {
            showSnackbar('Fehler beim Ändern des Sperrstatus.', 'error');
        });
    }

    function handleRemoveOrgCode(orgCode) {
        AdminData.removeOrgCode(editTenant.T_ID, orgCode).then(() => {
            setOrgCodes(prev => prev.filter(oc => oc.TOC_OrgCode !== orgCode));
        }).catch(() => {
            showSnackbar('Fehler beim Entfernen des OrgCodes.', 'error');
        });
    }

    if (accessAllowed === null) {
        return (
            <div>
                <AdminNavbar initValue={"/admin/tenants"}/>
                <Box sx={{display: 'flex', justifyContent: 'center', marginTop: '40px'}}>
                    <CircularProgress/>
                </Box>
            </div>
        );
    }

    if (!accessAllowed) {
        return (
            <div>
                <AdminNavbar initValue={"/admin/users"}/>
                <Box sx={{padding: '20px'}}>
                    <Typography color="text.secondary">Kein Zugriff auf die Tenant-Verwaltung.</Typography>
                </Box>
            </div>
        );
    }

    return (
        <div>
            <AdminNavbar initValue={"/admin/tenants"}/>
            <Box sx={{padding: '20px'}}>
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px'}}>
                    <Typography variant={"h6"}>Tenant-Verwaltung</Typography>
                    <Button variant={"contained"} endIcon={<AddIcon/>} onClick={handleOpenAdd}>
                        Tenant hinzufügen
                    </Button>
                </Box>

                {loading ? (
                    <Box sx={{display: 'flex', justifyContent: 'center', marginTop: '40px'}}>
                        <CircularProgress/>
                    </Box>
                ) : (
                    <TableContainer component={Paper} elevation={3}>
                        <Table size={"small"}>
                            <TableHead>
                                <TableRow sx={{'& th': {fontWeight: 'bold', backgroundColor: '#f5f5f5'}}}>
                                    <TableCell>ID</TableCell>
                                    <TableCell>Code</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell align={"center"}>Sichtbarkeit</TableCell>
                                    <TableCell align={"center"}>Zugang</TableCell>
                                    <TableCell align={"center"}>Aktion</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {tenants.map(tenant => (
                                    <TableRow key={tenant.T_ID} hover sx={tenant.T_Hidden ? {opacity: 0.6} : {}}>
                                        <TableCell>{tenant.T_ID}</TableCell>
                                        <TableCell>{tenant.T_Code}</TableCell>
                                        <TableCell>{tenant.T_Name}</TableCell>
                                        <TableCell align={"center"}>
                                            <Tooltip title={tenant.T_Hidden ? "Versteckt (Benutzer nicht in Admin-Liste)" : "Sichtbar"}>
                                                <IconButton size={"small"} onClick={() => handleToggleHidden(tenant)}
                                                            color={tenant.T_Hidden ? "default" : "primary"}>
                                                    {tenant.T_Hidden
                                                        ? <VisibilityOffIcon fontSize={"small"}/>
                                                        : <VisibilityIcon fontSize={"small"}/>
                                                    }
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell align={"center"}>
                                            <Tooltip title={tenant.T_Locked ? "Gesperrt (Benutzer können sich nicht anmelden)" : "Freigeschaltet"}>
                                                <IconButton size={"small"} onClick={() => handleToggleLocked(tenant)}
                                                            color={tenant.T_Locked ? "error" : "default"}>
                                                    {tenant.T_Locked
                                                        ? <LockIcon fontSize={"small"}/>
                                                        : <LockOpenIcon fontSize={"small"}/>
                                                    }
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell align={"center"}>
                                            <IconButton size={"small"} onClick={() => handleOpenEdit(tenant)}>
                                                <EditIcon fontSize={"small"}/>
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {tenants.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} align={"center"} sx={{color: 'text.secondary'}}>
                                            Keine Tenants vorhanden.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>

            {/* Add Tenant Dialog */}
            <Dialog open={addOpen} onClose={handleCloseAdd} maxWidth={"sm"} fullWidth>
                <DialogTitle>Neuen Tenant anlegen</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{marginTop: '8px'}}>
                        <TextField
                            label={"Code (T_Code)"}
                            value={newCode}
                            onChange={e => setNewCode(e.target.value)}
                            required
                            fullWidth
                            variant={"outlined"}
                        />
                        <TextField
                            label={"Name (T_Name)"}
                            value={newName}
                            onChange={e => setNewName(e.target.value)}
                            required
                            fullWidth
                            variant={"outlined"}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseAdd} style={{color: 'gray'}}>Abbrechen</Button>
                    <Button
                        variant={"contained"}
                        onClick={handleAddTenant}
                        disabled={!newCode.trim() || !newName.trim() || addSaving}
                    >
                        {addSaving ? <CircularProgress size={20}/> : 'Anlegen'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Tenant Dialog */}
            <Dialog open={editOpen} onClose={handleCloseEdit} maxWidth={"sm"} fullWidth>
                <DialogTitle>Tenant bearbeiten</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{marginTop: '8px'}}>
                        <TextField
                            label={"Code (T_Code)"}
                            value={editCode}
                            onChange={e => setEditCode(e.target.value)}
                            required
                            fullWidth
                            variant={"outlined"}
                        />
                        <TextField
                            label={"Name (T_Name)"}
                            value={editName}
                            onChange={e => setEditName(e.target.value)}
                            required
                            fullWidth
                            variant={"outlined"}
                        />

                        <Divider/>

                        <Typography variant={"subtitle2"}>OrgCodes</Typography>

                        {orgCodesLoading ? (
                            <CircularProgress size={24}/>
                        ) : (
                            <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1, minHeight: '32px'}}>
                                {orgCodes.map(oc => (
                                    <Chip
                                        key={oc.TOC_OrgCode}
                                        label={oc.TOC_OrgCode}
                                        onDelete={() => handleRemoveOrgCode(oc.TOC_OrgCode)}
                                        size={"small"}
                                    />
                                ))}
                                {orgCodes.length === 0 && (
                                    <Typography variant={"body2"} color={"text.secondary"}>
                                        Keine OrgCodes zugewiesen.
                                    </Typography>
                                )}
                            </Box>
                        )}

                        <Box sx={{display: 'flex', gap: 1}}>
                            <TextField
                                label={"OrgCode hinzufügen"}
                                value={newOrgCode}
                                onChange={e => setNewOrgCode(e.target.value)}
                                size={"small"}
                                onKeyDown={e => { if (e.key === 'Enter') handleAddOrgCode(); }}
                                fullWidth
                                variant={"outlined"}
                            />
                            <Button
                                variant={"outlined"}
                                onClick={handleAddOrgCode}
                                disabled={!newOrgCode.trim()}
                                sx={{whiteSpace: 'nowrap'}}
                            >
                                Hinzufügen
                            </Button>
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseEdit} style={{color: 'gray'}}>Abbrechen</Button>
                    <Button
                        variant={"contained"}
                        onClick={handleSaveTenant}
                        disabled={!editCode.trim() || !editName.trim() || editSaving}
                    >
                        {editSaving ? <CircularProgress size={20}/> : 'Speichern'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbarOpen} autoHideDuration={5000} onClose={() => setSnackbarOpen(false)}
                      anchorOrigin={{vertical: 'top', horizontal: 'right'}} TransitionComponent={Grow}>
                <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{width: '100%'}}>
                    {snackbarText}
                </Alert>
            </Snackbar>
        </div>
    );
}

export default AdminTenantsPage;
